import { LicenseTier } from '../interfaces/application';
import { OrganizationRef, Organization, OrganizationRole } from '../interfaces/organization';
import { User } from '../interfaces/user';

export type FeaturePermission =
  | 'workflow-import'
  | 'workflow-debug'
  | 'workflow-delete'
  | 'workflow-deactivate'
  | 'groups-manage'
  | 'claude-prompt';

type FeaturePolicy = {
  tiers?: LicenseTier[];
  roles?: OrganizationRole[];
  /**
   * If true, both tier AND role must be satisfied. If false, either is sufficient.
   * If omitted, defaults to true (require both).
   */
  requireBoth?: boolean;
  /**
   * If true, a personal organization owned by the user is granted this feature
   * regardless of tier. Useful for developer/debug features in single-user orgs.
   */
  allowPersonalOwner?: boolean;
};

const FEATURE_POLICIES: Record<FeaturePermission, FeaturePolicy> = {
  'workflow-import': { tiers: ['free', 'pro', 'enterprise'], roles: ['owner', 'admin'], requireBoth: true },
  'workflow-debug': { tiers: ['pro', 'enterprise'], roles: ['owner', 'admin'], requireBoth: true, allowPersonalOwner: true },
  'workflow-delete': { tiers: ['free', 'pro', 'enterprise'], roles: ['owner', 'admin'], requireBoth: true },
  'workflow-deactivate': { tiers: ['free', 'pro', 'enterprise'], roles: ['owner', 'admin'], requireBoth: true },
  'groups-manage': { tiers: ['pro', 'enterprise'], roles: ['owner', 'admin'], requireBoth: true },
  'claude-prompt': { tiers: ['free', 'pro', 'enterprise'], roles: ['owner', 'admin', 'member'], requireBoth: true },
};

class AccessController {

  private org: Organization | null = null;

  setOrganization(org: Organization | null): void {
    this.org = org;
  }

  getOrganization(): Organization | null {
    return this.org;
  }

  /**
   * Returns true if the organization's license tier, the authenticated user,
   * and the given org role all grant access to the feature.
   * orgRole is optional; if omitted, only the license tier and user presence are checked.
   * requireBoth controls whether tier AND role must both be satisfied (true, default)
   * or whether either one is sufficient (false). Has no effect when orgRole is omitted.
   */
  allows(feature: FeaturePermission, org: OrganizationRef | null, user: User | null, orgRole?: OrganizationRole, requireBoth?: boolean): boolean {
    if (!feature || !org || !user) return false;
    const policy = FEATURE_POLICIES[feature];
    if (!policy) return false;
    const tier = org.licenseTier;
    const tierAllowed = !!policy.tiers && policy.tiers.includes(tier);

    // If allowed for personal org owners, grant immediately when conditions match.
    try {
      const asAny = org as any;
      if (policy.allowPersonalOwner && asAny.visibility === 'personal' && asAny.ownerId && user.id && asAny.ownerId === user.id) {
        return true;
      }
    } catch (err) { /* ignore and continue */ }

    if (orgRole === undefined) return tierAllowed;
    const roleAllowed = !!policy.roles && policy.roles.includes(orgRole);
    const combineBoth = requireBoth !== undefined ? requireBoth : (policy.requireBoth !== undefined ? policy.requireBoth : true);
    return combineBoth ? tierAllowed && roleAllowed : tierAllowed || roleAllowed;
  }

}

export const Access = new AccessController();
